import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, UserPlus, Pencil, Trash2, Search, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

export default function StudentManagement({ user, logout }) {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    state: "",
    license_number: "",
    agd_number: "",
    password: ""
  });

  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/admin/students`, { headers });
      setStudents(response.data);
    } catch (error) {
      toast.error("Error loading students");
    }
  };

  const createStudent = async () => {
    if (!formData.name || !formData.email || !formData.state || !formData.license_number || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const form = new URLSearchParams();
      form.append("name", formData.name);
      form.append("email", formData.email);
      form.append("state", formData.state);
      form.append("license_number", formData.license_number);
      if (formData.agd_number) form.append("agd_number", formData.agd_number);
      form.append("password", formData.password);

      await axios.post(`${API}/admin/students?${form}`, {}, { headers });
      toast.success("Student created successfully");
      setShowCreateDialog(false);
      setFormData({ name: "", email: "", state: "", license_number: "", agd_number: "", password: "" });
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error creating student");
    }
  };

  const updateStudent = async () => {
    if (!formData.name || !formData.email || !formData.state || !formData.license_number) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const form = new URLSearchParams();
      form.append("name", formData.name);
      form.append("email", formData.email);
      form.append("state", formData.state);
      form.append("license_number", formData.license_number);
      if (formData.agd_number) form.append("agd_number", formData.agd_number);

      await axios.put(`${API}/admin/students/${selectedStudent.id}?${form}`, {}, { headers });
      toast.success("Student updated successfully");
      setShowEditDialog(false);
      setSelectedStudent(null);
      setFormData({ name: "", email: "", state: "", license_number: "", agd_number: "", password: "" });
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error updating student");
    }
  };

  const deleteStudent = async () => {
    try {
      await axios.delete(`${API}/admin/students/${selectedStudent.id}`, { headers });
      toast.success("Student deleted successfully");
      setShowDeleteDialog(false);
      setSelectedStudent(null);
      fetchStudents();
    } catch (error) {
      toast.error("Error deleting student");
    }
  };

  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.license_number && s.license_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.agd_number && s.agd_number.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-md border-b-4" style={{ borderColor: '#9E3639' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-12" />
            <div>
              <h1 className="text-2xl font-bold" style={{ color: '#092036' }}>
                Student Management
              </h1>
              <p className="text-sm text-slate-600">Add, edit, or remove student accounts</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/admin")}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Admin
            </Button>
            <Button
              variant="outline"
              onClick={logout}
              className="gap-2"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: '#092036' }}>
              All Students
            </h2>
            <p className="text-slate-600">{students.length} student(s) registered</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="gap-2 text-white" style={{ background: '#102862' }}>
                <UserPlus className="w-4 h-4" />
                Add Student
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Student</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Dr. John Smith"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="john@example.com"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>State *</Label>
                    <select
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option value="">Select State</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>License Number *</Label>
                    <Input
                      value={formData.license_number}
                      onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                      placeholder="12345"
                    />
                  </div>
                </div>
                <div>
                  <Label>AGD Number (Optional)</Label>
                  <Input
                    value={formData.agd_number}
                    onChange={(e) => setFormData({...formData, agd_number: e.target.value})}
                    placeholder="AGD123456"
                  />
                </div>
                <div>
                  <Label>Temporary Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Minimum 6 characters"
                  />
                  <p className="text-xs text-slate-500 mt-1">Student can change this after first login</p>
                </div>
                <Button onClick={createStudent} className="w-full text-white" style={{ background: '#102862' }}>
                  Create Student
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name, email, license, or AGD number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Students List */}
        {filteredStudents.length === 0 ? (
          <Card className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600">No students found</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudents.map(student => (
              <Card key={student.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg" style={{ background: '#102862' }}>
                    {student.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedStudent(student);
                        setFormData({
                          name: student.name,
                          email: student.email,
                          state: student.state || "",
                          license_number: student.license_number || "",
                          agd_number: student.agd_number || "",
                          password: ""
                        });
                        setShowEditDialog(true);
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedStudent(student);
                        setShowDeleteDialog(true);
                      }}
                      className="h-8 w-8 p-0 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
                <h3 className="font-bold text-lg mb-1" style={{ color: '#092036' }}>{student.name}</h3>
                <p className="text-sm text-slate-600 mb-3">{student.email}</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-500">State:</span>
                    <span className="font-semibold">{student.state || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">License:</span>
                    <span className="font-mono">{student.license_number || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">AGD:</span>
                    <span className="font-mono">{student.agd_number || "—"}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Student</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>State *</Label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Select State</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>License Number *</Label>
                <Input
                  value={formData.license_number}
                  onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>AGD Number (Optional)</Label>
              <Input
                value={formData.agd_number}
                onChange={(e) => setFormData({...formData, agd_number: e.target.value})}
              />
            </div>
            <Button onClick={updateStudent} className="w-full text-white" style={{ background: '#102862' }}>
              Update Student
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Student</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedStudent?.name}</strong>? This will permanently delete:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Student account</li>
                <li>All course progress</li>
                <li>Assessment results</li>
                <li>Survey responses</li>
                <li>Certificates</li>
              </ul>
              <p className="mt-2 text-red-600 font-semibold">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteDialog(false);
              setSelectedStudent(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={deleteStudent} className="bg-red-600 hover:bg-red-700">
              Delete Student
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
